import React, { MouseEventHandler } from 'react'
import { observer } from 'mobx-react-lite'
import { Card, Stack, useTheme } from '@mui/material'
import { Text } from '@mxenabled/mxui'

import { useScreenSize, useGlobalCopyStore, useGoalStore } from '../../common'
import { getPayoffProjection } from '../utils/SnowballCard'
import { DebtChartData } from '../../core/utils/DebtUtils'
import { useDebtsUiStore } from '../hooks/useDebtsUiStore'

interface SnowballCardProps {
  debts: DebtChartData[]
  onClickCta: MouseEventHandler
  sx?: any
}

const SnowballCard = ({ debts, onClickCta, sx }: SnowballCardProps) => {
  const theme = useTheme()
  const { isMobile } = useScreenSize()
  const { debts: copy } = useGlobalCopyStore()
  const { selectedDebtPriority } = useDebtsUiStore()
  const { monthlyCashFlowProfile } = useGoalStore()

  const { payoffDate, payoffSavings, payoffDuration } = React.useMemo(() => {
    const extraPaydown = monthlyCashFlowProfile?.extra_payment ?? 0
    return getPayoffProjection(debts, selectedDebtPriority, extraPaydown)
  }, [debts, selectedDebtPriority, monthlyCashFlowProfile])

  return (
    <Card
      sx={{
        boxShadow: theme.shadows[2],
        width: isMobile ? '100%' : '186px',
        minWidth: '186px',
        ...sx,
      }}
    >
      <Stack sx={{ gap: '12px', p: '16px' }}>
        <Stack
          gap={isMobile ? '4px' : '0'}
          sx={{
            borderBottom: '1px solid',
            borderBottomColor: theme.palette.grey[300],
            pb: '8px',
          }}
        >
          <Text variant="caption">{copy.snowball_card_date_description}</Text>
          <Text bold={true} color={theme.palette.primary.main} variant="body2">
            {payoffDate}
          </Text>
        </Stack>
        <Stack gap={isMobile ? '4px' : '0'}>
          <Text bold={true} variant="body2">
            {payoffSavings}
          </Text>
          <Text variant="caption">{copy.snowball_card_amount_description}</Text>
        </Stack>
        <Stack gap={isMobile ? '4px' : '0'}>
          <Text bold={true} variant="body2">
            {payoffDuration}
          </Text>
          <Text variant="caption">{copy.snowball_card_duration_description}</Text>
        </Stack>
        <Text
          bold={true}
          color={theme.palette.primary.main}
          onClick={onClickCta}
          sx={{ cursor: 'pointer' }}
          variant="body2"
        >
          {copy.snowball_card_cta}
        </Text>
      </Stack>
    </Card>
  )
}

export default observer(SnowballCard)
